import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Box,
  Grid,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

const Settings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  
  const [darkMode, setDarkMode] = useState(false);
  const [notifications, setNotifications] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [language, setLanguage] = useState('zh');
  const [autoPlay, setAutoPlay] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    // 模拟保存设置
    setTimeout(() => {
      setLoading(false);
      alert('设置已保存');
    }, 1000);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4, px: isMobile ? 1 : 2 }}>
      <Typography 
        variant={isMobile ? 'h4' : 'h3'} 
        component="h1" 
        sx={{ mb: 3, fontSize: isMobile ? '1.75rem' : '2.125rem' }}
      >
        系统设置
      </Typography>

      <Grid container spacing={3}>
        {/* 账户设置 */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: isMobile ? 2 : 4, borderRadius: 2 }}>
            <Typography 
              variant={isMobile ? 'h6' : 'h5'} 
              component="h2" 
              sx={{ mb: 3, fontSize: isMobile ? '1.25rem' : '1.5rem' }}
            >
              账户设置
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              <TextField
                fullWidth
                label="用户名"
                value="testuser"
                disabled
                InputProps={{
                  startAdornment: <AccountCircleIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ mb: 2, fontSize: isMobile ? '0.875rem' : '1rem' }}
              />
              
              <TextField
                fullWidth
                label="邮箱地址"
                type="email"
                value="test@example.com"
                disabled
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ mb: 2, fontSize: isMobile ? '0.875rem' : '1rem' }}
              />
              
              <TextField
                fullWidth
                label="密码"
                type="password"
                value="********"
                InputProps={{
                  startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ mb: 2, fontSize: isMobile ? '0.875rem' : '1rem' }}
              />
            </Box>
            
            <Button
              variant="outlined"
              fullWidth
              sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
              onClick={() => alert('密码修改功能开发中')}
            >
              修改密码
            </Button>
          </Paper>
        </Grid>

        {/* 通知设置 */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: isMobile ? 2 : 4, borderRadius: 2 }}>
            <Typography 
              variant={isMobile ? 'h6' : 'h5'} 
              component="h2" 
              sx={{ mb: 3, fontSize: isMobile ? '1.25rem' : '1.5rem' }}
            >
              通知设置
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notifications}
                    onChange={(e) => setNotifications(e.target.checked)}
                  />
                }
                label="通知总开关"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    disabled={!notifications}
                  />
                }
                label="邮件通知"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={pushNotifications}
                    onChange={(e) => setPushNotifications(e.target.checked)}
                    disabled={!notifications}
                  />
                }
                label="推送通知"
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <NotificationsIcon sx={{ mr: 2, color: 'text.secondary' }} />
              <Typography 
                variant="body1"
                fontSize={isMobile ? '0.875rem' : '1rem'}
              >
                通知提醒设置
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* 外观设置 */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: isMobile ? 2 : 4, borderRadius: 2 }}>
            <Typography 
              variant={isMobile ? 'h6' : 'h5'} 
              component="h2" 
              sx={{ mb: 3, fontSize: isMobile ? '1.25rem' : '1.5rem' }}
            >
              外观设置
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={darkMode}
                    onChange={(e) => setDarkMode(e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {darkMode ? <Brightness4Icon sx={{ mr: 1 }} /> : <Brightness7Icon sx={{ mr: 1 }} />}
                    深色模式
                  </Box>
                }
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={autoPlay}
                    onChange={(e) => setAutoPlay(e.target.checked)}
                  />
                }
                label="自动播放"
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LanguageIcon sx={{ mr: 2, color: 'text.secondary' }} />
              <Typography 
                variant="body1"
                fontSize={isMobile ? '0.875rem' : '1rem'}
              >
                语言: 中文
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* 隐私安全 */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: isMobile ? 2 : 4, borderRadius: 2 }}>
            <Typography 
              variant={isMobile ? 'h6' : 'h5'} 
              component="h2" 
              sx={{ mb: 3, fontSize: isMobile ? '1.25rem' : '1.5rem' }}
            >
              隐私安全
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={true}
                    disabled
                  />
                }
                label="记住登录状态"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={true}
                    disabled
                  />
                }
                label="允许数据分析"
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SecurityIcon sx={{ mr: 2, color: 'text.secondary' }} />
              <Typography 
                variant="body1"
                fontSize={isMobile ? '0.875rem' : '1rem'}
              >
                安全设置
              </Typography>
            </Box>
            
            <Button
              variant="outlined"
              fullWidth
              sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
              onClick={() => alert('清除缓存功能开发中')}
            >
              清除缓存
            </Button>
          </Paper>
        </Grid>

        {/* 保存设置 */}
        <Grid item xs={12}>
          <Paper sx={{ p: isMobile ? 2 : 4, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography 
                variant={isMobile ? 'h6' : 'h5'} 
                component="h2" 
                sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem' }}
              >
                保存设置
              </Typography>
              
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={loading}
                sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
              >
                {loading ? '保存中...' : '保存设置'}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Settings;
